import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import DatePicker from "../../shared/DatePicker/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function EmployeeCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [editIdProofPreview, setEditIdProofPreview] = useState<string | null>(
    null,
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      phone: "",
      familyContactNo: "",
      gender: "",
      bloodGroup: "",
      address: "",
      joiningDate: "",
      status: "Active",
      salary: "",
      employmentType: "",
      photo: null,
      idProof: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string().required("Phone number is required"),
      familyContactNo: Yup.string().required(
        "Family contact number is required",
      ),
      gender: Yup.string().required("Gender is required"),
      bloodGroup: Yup.string().required("Blood group is required"),
      address: Yup.string().required("Address is required"),
      joiningDate: Yup.string().required("Joining date is required"),
      salary: Yup.string().required("Salary is required"),
      employmentType: Yup.string().required("Employment type is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("phone", values.phone);
      formData.append("familyContactNo", values.familyContactNo);
      formData.append("gender", values.gender);
      formData.append("bloodGroup", values.bloodGroup);
      formData.append("address", values.address);
      formData.append("joiningDate", values.joiningDate);
      formData.append("status", values.status);
      formData.append("salary", values.salary);
      formData.append("employmentType", values.employmentType);
      if (values.photo) {
        formData.append("photo", values.photo);
      }
      if (values.idProof) {
        formData.append("idProof", values.idProof);
      }

      try {
        let response;
        if (id) {
          formData.append("id", id);
          response = await request.post("api/updateEmployee", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("api/createEmployee", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success("Employee saved successfully!");
          navigate("/admin/employees");
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    },
  });

  useEffect(() => {
    if (id) {
      const fetchEmployee = async () => {
        setLoading(true);
        try {
          const response = await request.get(`api/getEmployeeById/${id}`);
          const employee = response.data;
          formik.setValues({
            name: employee.name || "",
            phone: employee.phone || "",
            familyContactNo: employee.familyContactNo || "",
            gender: employee.gender || "",
            bloodGroup: employee.bloodGroup || "",
            address: employee.address || "",
            joiningDate: employee.joiningDate || "",
            status: employee.status || "Active",
            salary: employee.salary?.toString() || "",
            employmentType: employee.employmentType || "",
            photo: null,
            idProof: null,
          });
          setEditPhotoPreview(employee.photo_image_path || null);
          setEditIdProofPreview(employee.idProof_image_path || null);
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchEmployee();
    }
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={
                id && id !== "new" ? "Edit Employee" : "Create New Employee"
              }
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/employees"),
              }}
              primaryButton={{
                label:
                  id && id !== "new" ? "Update Employee" : "Create Employee",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField
                    name="name"
                    label="Name"
                    type="text"
                    formik={formik}
                    autoFocus
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="phone"
                    label="Phone"
                    type="text"
                    formik={formik}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="familyContactNo"
                    label="Family Contact No"
                    type="text"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="gender"
                    label="Gender"
                    formik={formik}
                    options={[
                      { value: "Male", label: "Male" },
                      { value: "Female", label: "Female" },
                      { value: "Other", label: "Other" },
                    ]}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <SelectField
                    name="bloodGroup"
                    label="Blood Group"
                    formik={formik}
                    options={[
                      { value: "A+", label: "A+" },
                      { value: "A-", label: "A-" },
                      { value: "B+", label: "B+" },
                      { value: "B-", label: "B-" },
                      { value: "AB+", label: "AB+" },
                      { value: "AB-", label: "AB-" },
                      { value: "O+", label: "O+" },
                      { value: "O-", label: "O-" },
                    ]}
                  />
                </Box>
                <Box className="form-field">
                  <DatePicker
                    name="joiningDate"
                    label="Joining Date"
                    formik={formik}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="salary"
                    label="Salary"
                    type="number"
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="employmentType"
                    label="Employment Type"
                    formik={formik}
                    options={[
                      { value: "Full Time", label: "Full Time" },
                      { value: "Part Time", label: "Part Time" },
                      { value: "Contract", label: "Contract" },
                    ]}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <TextField
                    name="address"
                    label="Address"
                    type="text"
                    multiline
                    rows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="status"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
              <Box
                className="form-grid form-grid--2"
                style={{ marginTop: "16px" }}
              >
                <Box className="form-field">
                  <FileUpload
                    name="photo"
                    formik={formik}
                    label="Upload Photo"
                    editImagePreview={editPhotoPreview}
                  />
                </Box>
                <Box className="form-field">
                  <FileUpload
                    name="idProof"
                    formik={formik}
                    label="Upload ID Proof"
                    editImagePreview={editIdProofPreview}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}
